import React, { useEffect } from 'react';
import { Box, Card, Flexbox, Link, Typography } from '@hiiretail/synergy-ui';
import styled from 'styled-components';
import { NewPageWrapper } from '../../common';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { useGetTenantAlias } from '../../common/hooks/useGetTenantAlias/useGetTenantAlias';

const DocBox = styled(Flexbox)`
  background-color: white;
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing(4)}px;
`;

export const Home: React.FC = () => {
  const { updateActions } = useBreadcrumbsActions();
  const alias = useGetTenantAlias();

  useEffect(() => {
    updateActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NewPageWrapper>
      <Flexbox container item justifyContent="center" direction="column">
        <Card width="100%">
          <Typography variant="header2" textAlign="center">
            Welcome to Identity and Access Management
          </Typography>
          <Flexbox container justifyContent="center" width="100%">
            <Flexbox item container width="85%">
              <Typography variant="body2">
                HIi Retail Identity and Access Management (IAM) enables you to manage access to Hii
                Retail services and resources securely. Using IAM, you can create and manage users
                and groups, and use permissions to allow and deny their access to Hii Retail
                resources.
              </Typography>
            </Flexbox>
          </Flexbox>
        </Card>
        <Box mb={4} />
        <Typography variant="header3">IAM Concepts:</Typography>
        <Flexbox container direction="column" gutter={1}>
          <Flexbox item container alignItems="stretch" gutter={2}>
            <Flexbox item container alignItems="stretch" flex="0.5 1 auto">
              <DocBox container direction="column" justifyContent="space-between">
                <Flexbox item>
                  <Typography variant="header4">
                    {alias === 'extenda' ? (
                      <Link to="/tenants">Tenants</Link>
                    ) : (
                      <Link to="/my-tenant">Tenants</Link>
                    )}
                  </Typography>
                </Flexbox>
                <Flexbox item>
                  <Typography variant="body2">
                    Hii Retail is a multi-tenant retail platform. A Hii Retail Tenant represents an
                    isolated resource boundary on the platform. A Tenant can manage their resources
                    but cannot access another Tenant's resources in any way. A single Tenant is
                    typically created for each Hii Retail customer. <br />
                  </Typography>
                  <Box mt={4} />
                  <Link
                    to="https://developer.hiiretail.com/docs/identity-and-access-management/public/concepts/tenants"
                    external
                  >
                    Read more
                  </Link>
                </Flexbox>
              </DocBox>
            </Flexbox>
            <Flexbox item container alignItems="stretch" flex="0.5 1 auto">
              <DocBox container direction="column" justifyContent="space-between">
                <Flexbox item>
                  <Typography variant="header4">
                    <Link to="/identity-providers">Identity Providers</Link>
                  </Typography>
                </Flexbox>
                <Flexbox item>
                  <Typography variant="body2">
                    Hii Retail IAM supports authentication by a federated identity provider. It
                    allows users to authenticate to Hii Retail using the credentials already
                    familiar to them for systems like Microsoft Azure AD, Okta, or Ping Federate.
                  </Typography>
                  <Box mt={4} />
                  <Link
                    to="https://developer.hiiretail.com/docs/identity-and-access-management/public/concepts/identity-providers"
                    external
                  >
                    Read more
                  </Link>
                </Flexbox>
              </DocBox>
            </Flexbox>
          </Flexbox>
          <Flexbox item container alignItems="stretch" gutter={2}>
            <Flexbox item container alignItems="stretch" flex="0.5 1 auto">
              <DocBox container direction="column" justifyContent="space-between">
                <Flexbox item>
                  <Typography variant="header4">
                    <Link to="/permissions">Permissions</Link> and{' '}
                    <Link to="/built-in-roles">Roles</Link>
                  </Typography>
                </Flexbox>
                <Flexbox item>
                  <Typography variant="body2">
                    Permissions and Roles are used to provide authorization controls for Hii Retail
                    resources. Authorization is the process of granting a User or Group of Users
                    access to one or more resources.
                    <br />
                    You can manage permissions in{' '}
                  </Typography>
                  <Box mt={4} />
                  <Link
                    to="https://developer.hiiretail.com/docs/identity-and-access-management/public/concepts/permissions-and-roles"
                    external
                  >
                    Read more
                  </Link>
                </Flexbox>
              </DocBox>
            </Flexbox>
            <Flexbox item container alignItems="stretch" flex="0.5 1 auto">
              <DocBox container direction="column" justifyContent="space-between">
                <Flexbox item>
                  <Typography variant="header4">
                    <Link to="/users">Users</Link> and <Link to="/groups">Groups</Link>
                  </Typography>
                </Flexbox>
                <Flexbox item>
                  <Typography variant="body2">
                    A User represents a specific named entity that can act on a resource. The User
                    identity can represent a human User like a store employee or back-office
                    administrator. The User identity can also represent a machine or service User
                    such as a point of sales endpoint. For more information about managing access
                    for machine or service-users see{' '}
                    <Link to="https://developer.hiiretail.com/docs/ocms/public/readme" external>
                      OCMS
                    </Link>
                  </Typography>
                  <Box mt={4} />
                  <Link
                    to="https://developer.hiiretail.com/docs/identity-and-access-management/public/concepts/user-groups"
                    external
                  >
                    Read more
                  </Link>
                </Flexbox>
              </DocBox>
            </Flexbox>
          </Flexbox>
        </Flexbox>
      </Flexbox>
    </NewPageWrapper>
  );
};
